import { DsSelectMenu } from "@/core/components/layout/ds-select-menu";
import { useCallback, useEffect, useMemo, useState } from "react";

interface LocationItem {
  id: string;
  name: string;
}

interface LocationSelectProps<T extends LocationItem> {
  id: string;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  /** Rótulo do campo com mais de um item selecionado. */
  multipleLabel: (count: number) => string;
  /** Opções já filtradas pela dependência cruzada (cidades ⇄ espaços). */
  options: T[];
  /** Seleção vinda do store (`filters.cities`/`filters.spaces`). */
  value: T[] | null | undefined;
  isLoading: boolean;
  /** Seleção vazia vira `null` ("Todas as cidades/Todos os espaços"). */
  onChange: (value: T[] | null) => void;
}

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/**
 * Multi-seleção de cidades/espaços da modal "Mais filtros" do Painel, no menu
 * com visual do InputSelect do Workbase DS (DsSelectMenu). Mantém a busca
 * local: a lista de espaços pode ser longa e o InputSelect do DS não tem
 * busca nem altura máxima no menu. Nenhum item selecionado = todos.
 */
export function LocationSelect<T extends LocationItem>({
  id,
  label,
  placeholder,
  searchPlaceholder,
  emptyText,
  multipleLabel,
  options,
  value,
  isLoading,
  onChange,
}: LocationSelectProps<T>) {
  const [selectedItem, setSelectedItem] = useState<T[] | null>(value ?? null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSelectedItem(value ?? null);
  }, [value]);

  const visibleOptions = useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return options;
    return options.filter((item) => normalize(item.name).includes(term));
  }, [options, search]);

  const handleSelect = useCallback(
    (optionId: string) => {
      const item = options.find((option) => option.id === optionId);
      if (!item) return;
      const current = selectedItem ?? [];
      const isSelected = current.some((selected) => selected.id === item.id);
      const next = isSelected
        ? current.filter((selected) => selected.id !== item.id)
        : [...current, item];
      const normalized = next.length > 0 ? next : null;
      setSelectedItem(normalized);
      onChange(normalized);
    },
    [options, selectedItem, onChange],
  );

  const displayValue =
    selectedItem && selectedItem.length > 0
      ? selectedItem.length === 1
        ? selectedItem[0].name
        : multipleLabel(selectedItem.length)
      : null;

  return (
    <DsSelectMenu
      id={id}
      label={label}
      placeholder={placeholder}
      displayValue={displayValue}
      multiple
      disabled={isLoading}
      loading={isLoading}
      options={visibleOptions.map((item) => ({
        value: item.id,
        label: item.name,
      }))}
      selected={selectedItem?.map((item) => item.id) ?? []}
      onSelect={(option) => handleSelect(option.value)}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: searchPlaceholder,
      }}
      emptyText={emptyText}
      onOpenChange={(open) => {
        if (!open) setSearch("");
      }}
    />
  );
}
